// =============================================================================
// Worksites API
// =============================================================================
import { apiClient } from './http';

/** Espejo de WorksiteResponse (backend/app/schemas/worksite.py). */
export interface Worksite {
  id: number;
  uuid: string;                    // lo que lleva el QR
  name: string;
  code: string;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  country?: string | null;
  postal_code?: string | null;
  geo_latitude?: number | null;
  geo_longitude?: number | null;
  geo_radius_meters?: number | null;
  require_geofence: boolean;
  require_dynamic_qr: boolean;
  min_staff?: number | null;
  contact_person_name?: string | null;
  is_active: boolean;
}

export async function getWorksites(): Promise<Worksite[]> {
  const response = await apiClient.get<Worksite[]>('/worksites');
  return response.data;
}

export interface WorksiteCreate {
  name: string;
  code: string;
  address?: string;
  geo_latitude?: number;
  geo_longitude?: number;
  geo_radius_meters?: number;
  require_geofence?: boolean;
  require_dynamic_qr?: boolean;
  min_staff?: number;
  contact_person_name?: string;
  city?: string;
  province?: string;
  country?: string;
  postal_code?: string;
}

export async function createWorksite(data: WorksiteCreate): Promise<Worksite> {
  const res = await apiClient.post<Worksite>('/worksites', data);
  return res.data;
}

export async function updateWorksite(
  id: number,
  data: Partial<WorksiteCreate> & { require_dynamic_qr?: boolean }
): Promise<Worksite> {
  const res = await apiClient.put<Worksite>(`/worksites/${id}`, data);
  return res.data;
}

// Un centro se rellena con un formulario (WorksiteFormValue) y se envía como
// WorksiteCreate. Este es el ÚNICO sitio donde se deriva la geovalla: exigida
// solo si hay radio Y coordenadas (una geovalla sin ubicar no bloquea a nadie).
export interface WorksiteFormValue {
  name: string; code: string;
  address?: string; city?: string; province?: string; country?: string; postal_code?: string;
  geo_latitude?: number; geo_longitude?: number;
  geo_radius_meters?: number; require_geofence?: boolean; require_dynamic_qr?: boolean;
  min_staff?: number; contact_person_name?: string;
}
// Precarga: un centro guardado (respuesta) → los valores del formulario. Los
// campos coinciden casi 1:1; lat/lon vienen como number|null y se pasan a
// number|undefined. Se conserva TODO lo guardado (ciudad/provincia/país/
// dirección/radio/pin/QR). Ojo: la cascada GeoNames se guía por códigos
// administrativos que NO guardamos (solo el NOMBRE), así que los desplegables
// de provincia/ciudad arrancan vacíos aunque province/city estén rellenos —
// se conservan en el draft y solo se pisan si el usuario re-selecciona.
export function toWorksiteFormValue(s: Worksite): WorksiteFormValue {
  return {
    name: s.name,
    code: s.code,
    address: s.address ?? undefined,
    city: s.city ?? undefined,
    province: s.province ?? undefined,
    country: s.country ?? undefined,
    postal_code: s.postal_code ?? undefined,
    geo_latitude: s.geo_latitude ?? undefined,
    geo_longitude: s.geo_longitude ?? undefined,
    geo_radius_meters: s.geo_radius_meters ?? 0,
    require_dynamic_qr: s.require_dynamic_qr,
    // Se precargaba con el resumen del listado, que NO traía estos campos:
    // abrir "Editar centro" y pulsar "Guardar" sin tocar nada apagaba la
    // geovalla y el QR de pantalla del centro. A partir de ahí se podía fichar
    // desde el sofá con una foto del QR estático y el fichaje contaba como
    // bueno. Ahora el listado devuelve el centro completo y esto lo conserva.
    min_staff: s.min_staff ?? undefined,
    contact_person_name: s.contact_person_name ?? undefined,
  };
}

export function toWorksiteCreate(v: WorksiteFormValue): WorksiteCreate {
  const radius = v.geo_radius_meters ?? 0;
  return {
    ...v,
    code: v.code || v.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'centro',
    // La geovalla sólo se deriva con radio Y las DOS coordenadas: una
    // latitud sin longitud guardaba un centro «con geovalla» que no estaba
    // ubicado y todos sus fichajes caían a revisión sin causa visible
    // (r2b10[5]). El backend replica el mismo criterio (422), así que las
    // dos capas dicen lo mismo. Las coordenadas se envían SIEMPRE (aunque
    // la geovalla esté apagada): conservar el pin del mapa es lo que hace
    // que reactivarla luego sea un gesto deliberado, no una resurrección.
    require_geofence: radius > 0 && v.geo_latitude != null && v.geo_longitude != null,
    require_dynamic_qr: !!v.require_dynamic_qr,
    geo_radius_meters: radius > 0 ? radius : undefined,
  };
}

// =============================================================================
// Alta de una tablet de quiosco
// =============================================================================
//
// El endpoint existía desde siempre y NO lo llamaba ninguna pantalla: en todo
// el frontend no había una sola escritura de `kakuat-kiosk-id`. El flujo real
// —admin da de alta la tablet, la tablet ficha— no existía: había que teclear
// el id en la consola del navegador del dispositivo.
export interface KioskRegistration {
  kiosk_id: string;
  tenant_id: number;
  location_name: string;
  worksite_id: number | null;
  worksite_uuid: string | null;
}

export async function registerKiosk(
  deviceId: string,
  locationName: string,
  worksiteId?: number,
): Promise<KioskRegistration> {
  const res = await apiClient.post<KioskRegistration>('/kiosk/register', {
    device_id: deviceId,
    location_name: locationName,
    worksite_id: worksiteId ?? null,
  });
  return res.data;
}

/**
 * PNG del QR de alta de tablet, generado por el backend.
 *
 * La URL lleva el `kiosk_id` —una credencial de fichaje— y antes se mandaba a
 * api.qrserver.com para que un tercero la codificara: fuga de credencial sin
 * contrato y fuera de la CSP (ronda 3, r2b10/r2b21). Se devuelve como data
 * URL para poder meterla en un <img> de una ventana nueva sin exponer el
 * token en una query.
 */
export async function fetchKioskQr(
  worksiteId: number,
  url: string,
): Promise<string> {
  const res = await apiClient.post(
    `/worksites/${worksiteId}/kiosk-qr`,
    { url },
    { responseType: 'blob' },
  );

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(res.data as Blob);
  });
}

/**
 * PNG del QR de PAPEL (estático) del centro, listo para imprimir.
 *
 * El endpoint exige ADMIN/MANAGER vía Bearer: la ventana de impresión lo pedía
 * con un `<img src="/api/v1/...">` sin Authorization — 401, imagen rota y un
 * `onload` que jamás disparaba el diálogo de imprimir (ronda 3, r2b21[4]).
 * Se devuelve como Blob para montarlo con URL.createObjectURL.
 */
export async function fetchPrintableQr(worksiteId: number): Promise<Blob> {
  const res = await apiClient.get<Blob>(`/worksites/${worksiteId}/qr/imprimible`, {
    responseType: 'blob',
  });
  return res.data;
}
